import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import memoryApi from '../api/memoryApi';

/**
 * DatabaseCard - Site-wide RAG PDF management
 *
 * Location: /chatty/projects page (side panel)
 * Visibility: Admin/Owner only
 * Pattern: Same as Memory/Instruction/File cards in ProjectHome
 */
const DatabaseCard = ({ onOpenPdfViewer }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const csvInputRef = useRef(null);

  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMetadata, setUploadMetadata] = useState({ title: '', description: '', category: '' });
  // eslint-disable-next-line no-unused-vars
  const [uploadQueue, setUploadQueue] = useState([]); // Queue for multiple files (future use)
  const [selectedPdfIds, setSelectedPdfIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [updatingPdfIds, setUpdatingPdfIds] = useState(new Set()); // Track PDFs being updated via CSV
  const [csvPreview, setCsvPreview] = useState(null); // Preview CSV data before applying

  // Role-based visibility: check custom:membership from Cognito (admin, owner)
  // OR role for local dev (developer)
  const membership = user?.['custom:membership'];
  const role = user?.role;
  const canManageDatabase = ['owner', 'admin'].includes(membership) || role === 'developer';

  // Load PDFs on mount
  useEffect(() => {
    if (!canManageDatabase) {
      setLoading(false);
      return;
    }

    const loadPdfs = async () => {
      try {
        const pdfList = await memoryApi.listRagPdfs();
        setPdfs(pdfList);
      } catch (error) {
        console.error('Failed to load RAG PDFs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPdfs();
  }, [canManageDatabase]);

  if (!canManageDatabase) return null;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type === 'application/pdf');
    if (files.length === 0) return;

    if (files.length === 1) {
      // Single file - show modal for metadata
      setUploadFile(files[0]);
      setUploadMetadata({
        title: files[0].name.replace('.pdf', ''),
        description: '',
        category: ''
      });
      setShowUploadModal(true);
    } else {
      // Multiple files - queue them with auto-generated titles
      setUploadQueue(files.map(f => ({
        file: f,
        metadata: { title: f.name.replace('.pdf', ''), description: '', category: '' }
      })));
      handleBulkUpload(files);
    }
    e.target.value = '';
  };

  const handleBulkUpload = async (files) => {
    // Add skeleton cards immediately
    const skeletonPdfs = files.map((f, i) => ({
      file_id: `uploading_${Date.now()}_${i}`,
      filename: f.name,
      title: f.name.replace('.pdf', ''),
      uploading: true
    }));
    setPdfs(prev => [...prev, ...skeletonPdfs]);

    // Upload each file and replace skeleton with real data
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const skeletonId = skeletonPdfs[i].file_id;

      try {
        const result = await memoryApi.uploadRagPdf(file, {
          title: file.name.replace('.pdf', ''),
          description: '',
          category: ''
        });

        // Replace skeleton with real PDF
        setPdfs(prev => prev.map(p =>
          p.file_id === skeletonId
            ? { ...p, file_id: result.file_id, uploading: false }
            : p
        ));
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Remove failed skeleton
        setPdfs(prev => prev.filter(p => p.file_id !== skeletonId));
      }
    }

    setUploadQueue([]);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const result = await memoryApi.uploadRagPdf(uploadFile, uploadMetadata);

      // Add to local state
      const newPdf = {
        file_id: result.file_id,
        filename: uploadFile.name,
        title: uploadMetadata.title,
        description: uploadMetadata.description,
        category: uploadMetadata.category,
        cover_url: null, // No thumbnail yet (Phase 1)
        source: 'manual'
      };
      setPdfs(prev => [...prev, newPdf]);

      setShowUploadModal(false);
      setUploadFile(null);
      setUploadMetadata({ title: '', description: '', category: '' });
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        alert('CSV must have a header row and at least one data row');
        return;
      }

      // Parse header (expect: filename, title, description, category)
      const header = lines[0].split(',').map(h => h.trim().toLowerCase());
      const filenameIdx = header.indexOf('filename');
      const titleIdx = header.indexOf('title');
      const descIdx = header.indexOf('description');
      const categoryIdx = header.indexOf('category');

      if (filenameIdx === -1) {
        alert('CSV must have a "filename" column to match PDFs');
        return;
      }

      // Parse data rows
      const rows = [];
      const updates = [];
      for (let i = 1; i < lines.length; i++) {
        // Simple CSV parsing (handles quoted values)
        const values = [];
        let current = '';
        let inQuotes = false;
        for (const char of lines[i]) {
          if (char === '"') inQuotes = !inQuotes;
          else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const filename = values[filenameIdx];
        if (!filename) continue;

        const rowData = {
          filename,
          title: titleIdx !== -1 ? values[titleIdx] || '' : '',
          description: descIdx !== -1 ? values[descIdx] || '' : '',
          category: categoryIdx !== -1 ? values[categoryIdx] || '' : ''
        };
        rows.push(rowData);

        // Find matching PDF
        const matchingPdf = pdfs.find(p =>
          p.filename === filename ||
          p.filename === filename + '.pdf' ||
          p.filename?.replace('.pdf', '') === filename.replace('.pdf', '')
        );

        if (matchingPdf) {
          const metadata = {};
          if (rowData.title) metadata.title = rowData.title;
          if (rowData.description) metadata.description = rowData.description;
          if (rowData.category) metadata.category = rowData.category;

          if (Object.keys(metadata).length > 0) {
            updates.push({ pdf: matchingPdf, metadata, rowData });
          }
        }
      }

      // Show preview modal
      setCsvPreview({
        file,
        rows,
        updates,
        matched: updates.length,
        unmatched: rows.length - updates.length
      });
    } catch (error) {
      console.error('CSV import error:', error);
      alert('Failed to parse CSV: ' + error.message);
    }
  };

  const confirmCSVImport = async () => {
    if (!csvPreview) return;

    const { file, updates } = csvPreview;
    setCsvPreview(null);

    // Mark all matched PDFs as updating (show loading state)
    const updatingIds = new Set(updates.map(u => u.pdf.file_id));
    setUpdatingPdfIds(updatingIds);

    // Store CSV in S3
    try {
      const csvUploadResult = await memoryApi.uploadRagPdf(file, {
        title: `Metadata Import - ${new Date().toLocaleDateString()}`,
        description: `CSV import with ${updates.length} updates`,
        category: 'metadata-import'
      });
      console.log('CSV stored:', csvUploadResult);
    } catch (err) {
      console.error('Failed to store CSV:', err);
    }

    // Apply updates
    for (const { pdf, metadata } of updates) {
      try {
        await memoryApi.updateRagPdfMetadata(pdf.file_id, metadata);
        // Update local state and remove from updating set
        setPdfs(prev => prev.map(p =>
          p.file_id === pdf.file_id ? { ...p, ...metadata } : p
        ));
        setUpdatingPdfIds(prev => {
          const next = new Set(prev);
          next.delete(pdf.file_id);
          return next;
        });
      } catch (err) {
        console.error(`Failed to update ${pdf.filename}:`, err);
        setUpdatingPdfIds(prev => {
          const next = new Set(prev);
          next.delete(pdf.file_id);
          return next;
        });
      }
    }
  };

  const handleDeletePdf = async (fileId) => {
    try {
      await memoryApi.deleteRagPdf(fileId);
      setPdfs(prev => prev.filter(p => p.file_id !== fileId));
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed: ' + error.message);
    }
  };

  const handleOpenPdf = async (pdf) => {
    try {
      const result = await memoryApi.getRagPdfUrl(pdf.file_id);
      if (onOpenPdfViewer) {
        onOpenPdfViewer({ ...pdf, pdf_url: result.pdf_url });
      } else {
        // Fallback: open in new tab
        window.open(result.pdf_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to get PDF URL:', error);
      alert('Failed to open PDF: ' + error.message);
    }
  };

  const toggleSelectPdf = (fileId) => {
    setSelectedPdfIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const nonUploadingPdfs = pdfs.filter(p => !p.uploading);
    if (selectedPdfIds.size === nonUploadingPdfs.length) {
      setSelectedPdfIds(new Set());
    } else {
      setSelectedPdfIds(new Set(nonUploadingPdfs.map(p => p.file_id)));
    }
  };

  const handleDownloadSelected = async () => {
    for (const fileId of selectedPdfIds) {
      const pdf = pdfs.find(p => p.file_id === fileId);
      if (!pdf) continue;
      try {
        const result = await memoryApi.getRagPdfUrl(fileId);
        const link = document.createElement('a');
        link.href = result.pdf_url;
        link.download = result.filename || pdf.filename || 'document.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error(`Failed to download ${pdf.filename}:`, error);
      }
    }
  };

  const handleDeleteSelected = async () => {
    for (const fileId of selectedPdfIds) {
      try {
        await memoryApi.deleteRagPdf(fileId);
        setPdfs(prev => prev.filter(p => p.file_id !== fileId));
      } catch (error) {
        console.error(`Failed to delete ${fileId}:`, error);
      }
    }
    setSelectedPdfIds(new Set());
    setSelectionMode(false);
  };

  return (
    <>
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
        }}
      >
        {/* Database Section */}
        <div className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">Reference Database</h3>
            <div className="flex gap-1">
              {selectionMode ? (
                /* Selection mode controls */
                <>
                  <button
                    onClick={handleSelectAll}
                    className="px-2 py-1 rounded-md transition-colors text-xs"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {selectedPdfIds.size === pdfs.filter(p => !p.uploading).length ? 'Deselect' : 'Select All'}
                  </button>
                  {selectedPdfIds.size > 0 && (
                    <>
                      <button
                        onClick={handleDownloadSelected}
                        className="p-1 rounded-md transition-colors"
                        style={{ color: 'var(--accent-color, #3b82f6)' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Download selected"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button
                        onClick={handleDeleteSelected}
                        className="p-1 rounded-md transition-colors"
                        style={{ color: '#ef4444' }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Delete selected"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => { setSelectionMode(false); setSelectedPdfIds(new Set()); }}
                    className="px-2 py-1 rounded-md transition-colors text-xs"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                /* Normal mode controls */
                <>
                  {pdfs.length > 0 && (
                    <button
                      onClick={() => setSelectionMode(true)}
                      className="px-2 py-1 rounded-md transition-colors text-xs"
                      style={{ color: 'var(--text-secondary)' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Select multiple"
                    >
                      Select
                    </button>
                  )}
                  <button
                    onClick={() => csvInputRef.current?.click()}
                    className="px-2 py-1 rounded-md transition-colors text-xs"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Import metadata from CSV"
                  >
                    Metadata
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1 rounded-md transition-colors"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    title="Upload PDF"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            onChange={handleCSVImport}
            style={{ display: 'none' }}
          />

          {/* Loading state */}
          {loading ? (
            <div className="flex items-center gap-2 py-4">
              <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--text-secondary)', borderTopColor: 'transparent' }} />
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Loading...</span>
            </div>
          ) : pdfs.length > 0 ? (
            /* PDF thumbnails - horizontal scroll */
            <div className="flex gap-3 overflow-x-auto py-2">
              {pdfs.map(pdf => (
                <div
                  key={pdf.file_id}
                  className={`flex-shrink-0 w-20 cursor-pointer group relative ${pdf.uploading || updatingPdfIds.has(pdf.file_id) ? 'animate-pulse' : ''}`}
                  onClick={() => {
                    if (pdf.uploading) return;
                    if (selectionMode) {
                      toggleSelectPdf(pdf.file_id);
                    } else {
                      handleOpenPdf(pdf);
                    }
                  }}
                >
                  <div
                    className="w-full h-28 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: selectedPdfIds.has(pdf.file_id)
                        ? '2px solid var(--accent-color, #3b82f6)'
                        : '1px solid var(--border-color, rgba(255,255,255,0.1))'
                    }}
                  >
                    {/* Selection checkbox */}
                    {selectionMode && !pdf.uploading && (
                      <div
                        className="absolute top-1 left-1 w-5 h-5 rounded flex items-center justify-center z-10"
                        style={{
                          backgroundColor: selectedPdfIds.has(pdf.file_id) ? 'var(--accent-color, #3b82f6)' : 'rgba(0,0,0,0.5)',
                          border: selectedPdfIds.has(pdf.file_id) ? 'none' : '1px solid rgba(255,255,255,0.3)'
                        }}
                      >
                        {selectedPdfIds.has(pdf.file_id) && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    )}
                    {pdf.uploading || updatingPdfIds.has(pdf.file_id) ? (
                      /* Uploading/Updating spinner */
                      <svg className="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: 'var(--accent-color, #3b82f6)' }}>
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : pdf.cover_url ? (
                      <img src={pdf.cover_url} className="w-full h-full object-cover rounded-lg" alt={pdf.title} />
                    ) : (
                      <svg className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>
                  {pdf.uploading ? (
                    /* Skeleton title */
                    <div className="h-3 rounded w-3/4 mt-1" style={{ backgroundColor: 'var(--bg-secondary)' }} />
                  ) : (
                    <div className="text-xs truncate mt-1" style={{ color: updatingPdfIds.has(pdf.file_id) ? 'var(--text-secondary)' : 'var(--text-primary)' }}>
                      {pdf.title || pdf.filename}
                    </div>
                  )}
                  {/* Delete button on hover - hide while uploading or in selection mode */}
                  {!pdf.uploading && !selectionMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePdf(pdf.file_id);
                      }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white'
                      }}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Upload PDFs to build your reference database
            </p>
          )}
        </div>

        {/* Access Control Section */}
        <div
          className="p-5 border-t cursor-pointer transition-colors"
          style={{ borderColor: 'var(--border-color, rgba(255,255,255,0.1))' }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, rgba(255,255,255,0.03))'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm">Access Control</h3>
            <svg className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
            Manage who can access reference documents
          </p>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60"
            onClick={() => setShowUploadModal(false)}
          />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md p-6 rounded-2xl"
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
            }}
          >
            <h2 className="text-lg font-semibold mb-4">Upload PDF</h2>

            {/* File info */}
            <div
              className="flex items-center gap-3 p-3 rounded-lg mb-4"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              <svg className="w-8 h-8" style={{ color: 'var(--text-secondary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{uploadFile?.name}</div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {uploadFile && (uploadFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            </div>

            {/* Title input */}
            <div className="mb-3">
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Title</label>
              <input
                type="text"
                value={uploadMetadata.title}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder="Document title"
              />
            </div>

            {/* Description input */}
            <div className="mb-3">
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Description</label>
              <textarea
                value={uploadMetadata.description}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                rows={3}
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder="Brief description of the document"
              />
            </div>

            {/* Category input */}
            <div className="mb-4">
              <label className="block text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>Category</label>
              <input
                type="text"
                value={uploadMetadata.category}
                onChange={(e) => setUploadMetadata(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                placeholder="e.g., Safety, Legal, Training"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || !uploadMetadata.title.trim()}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* CSV Preview Modal */}
      {csvPreview && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60"
            onClick={() => setCsvPreview(null)}
          />
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg p-6 rounded-2xl max-h-[80vh] overflow-hidden flex flex-col"
            style={{
              backgroundColor: 'var(--bg-primary)',
              border: '1px solid var(--border-color, rgba(255,255,255,0.1))'
            }}
          >
            <h2 className="text-lg font-semibold mb-2">CSV Import Preview</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {csvPreview.file.name} - {csvPreview.rows.length} rows
            </p>

            {/* Summary */}
            <div className="flex gap-4 mb-4">
              <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-lg font-semibold" style={{ color: 'var(--accent-color, #22c55e)' }}>{csvPreview.matched}</div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Matched</div>
              </div>
              <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <div className="text-lg font-semibold" style={{ color: csvPreview.unmatched > 0 ? '#f59e0b' : 'var(--text-secondary)' }}>{csvPreview.unmatched}</div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Unmatched</div>
              </div>
            </div>

            {/* Updates list */}
            <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: '300px' }}>
              <div className="text-xs font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Updates to apply:</div>
              {csvPreview.updates.map((u, i) => (
                <div
                  key={i}
                  className="p-2 rounded-lg mb-2 text-sm"
                  style={{ backgroundColor: 'var(--bg-secondary)' }}
                >
                  <div className="font-medium truncate">{u.rowData.filename}</div>
                  {u.metadata.title && (
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Title: {u.metadata.title}
                    </div>
                  )}
                  {u.metadata.description && (
                    <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      Desc: {u.metadata.description}
                    </div>
                  )}
                  {u.metadata.category && (
                    <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Category: {u.metadata.category}
                    </div>
                  )}
                </div>
              ))}
              {csvPreview.updates.length === 0 && (
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No matching PDFs found. Make sure filenames in CSV match uploaded PDFs.
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCsvPreview(null)}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmCSVImport}
                disabled={csvPreview.updates.length === 0}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: 'var(--accent-color, #3b82f6)' }}
              >
                Apply {csvPreview.matched} Updates
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DatabaseCard;
